<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import KanbanDatePicker from './components/KanbanDatePicker.vue';
import ContactAPI from 'dashboard/api/contacts';
import { useNativeFieldOptions } from 'dashboard/composables/useNativeFieldOptions';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';

const props = defineProps({
  selectedIds: { type: Array, required: true },
  columns: { type: Array, default: () => [] },
});

const emit = defineEmits(['done', 'cancel', 'close']);

const { t } = useI18n();
const store = useStore();
const agents = computed(() => store.getters['agents/getAgents'] ?? []);
const accountLabels = computed(() => store.getters['labels/getLabels'] ?? []);
const count = computed(() => props.selectedIds.length);

// ── Staging area ──────────────────────────────────────────────────────────────
const pendingChanges = ref({});

const hasPendingChanges = computed(() => {
  const p = pendingChanges.value;
  return Object.keys(p).some(k => {
    if (k === '_contactName') return false;
    if (k === 'label_add' || k === 'label_remove')
      return (p[k]?.length ?? 0) > 0;
    if (k === 'task') return !!p[k]?.title;
    if (k === 'note') return !!p[k]?.content;
    return p[k] !== undefined && p[k] !== null;
  });
});

// ── Submenu ───────────────────────────────────────────────────────────────────
const activeSubmenu = ref(null);
const labelSearch = ref('');
const labelSearchRef = ref(null);
const labelRemoveSearchRef = ref(null);
const dateValue = ref({ due_date: null, due_time: null });

// ── Refs dos botões para calcular posição ─────────────────────────────────────
const columnBtnRef = ref(null);
const statusBtnRef = ref(null);
const assigneeBtnRef = ref(null);
const dateBtnRef = ref(null);
const valueBtnRef = ref(null);
const sourceBtnRef = ref(null);
const tagsAddBtnRef = ref(null);
const tagsRemoveBtnRef = ref(null);
const taskBtnRef = ref(null);
const noteBtnRef = ref(null);
const contactBtnRef = ref(null);

const columnSubmenuPos = ref({ top: 0, left: 0 });
const statusSubmenuPos = ref({ top: 0, left: 0 });
const assigneeSubmenuPos = ref({ top: 0, left: 0 });
const dateSubmenuPos = ref({ top: 0, left: 0 });
const valueSubmenuPos = ref({ top: 0, left: 0 });
const sourceSubmenuPos = ref({ top: 0, left: 0 });
const tagsAddSubmenuPos = ref({ top: 0, left: 0 });
const tagsRemoveSubmenuPos = ref({ top: 0, left: 0 });
const taskSubmenuPos = ref({ top: 0, left: 0 });
const noteSubmenuPos = ref({ top: 0, left: 0 });
const contactSubmenuPos = ref({ top: 0, left: 0 });

function calcSubmenuPos(btnRef, posRef, submenuWidth = 208) {
  if (!btnRef?.value) return;
  const rect = btnRef.value.getBoundingClientRect();
  const spaceRight = window.innerWidth - rect.right;
  if (spaceRight >= submenuWidth + 8) {
    posRef.value = { top: rect.top, left: rect.right + 4 };
  } else {
    posRef.value = { top: rect.top, left: rect.left - submenuWidth - 4 };
  }
}

// ── Avatar helpers ────────────────────────────────────────────────────────────
const { avatarColor, initials } = useAvatarColor();

const assigneeSearch = ref('');
const columnSearch = ref('');
const sourceSearch = ref('');
const assigneeHighlight = ref(0);
const columnHighlight = ref(0);
const sourceHighlight = ref(0);

const contactSearch = ref('');
const contactResults = ref([]);
const contactHighlight = ref(0);
const contactSearching = ref(false);

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

let contactSearchDebounce = null;
let contactSearchRequestId = 0;

async function performContactSearch(query) {
  contactSearchRequestId += 1;
  const requestId = contactSearchRequestId;
  contactSearching.value = true;
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    if (requestId !== contactSearchRequestId) return; // resposta obsoleta
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlight.value = 0;
  } catch {
    if (requestId === contactSearchRequestId) contactResults.value = [];
  } finally {
    if (requestId === contactSearchRequestId) contactSearching.value = false;
  }
}

// debounce evita disparar uma requisição a cada tecla digitada; o
// requestId acima também descarta respostas que cheguem fora de ordem.
function searchContacts(query) {
  clearTimeout(contactSearchDebounce);
  if (!query || query.length < 1) {
    contactSearchRequestId += 1;
    contactResults.value = [];
    return;
  }
  contactSearchDebounce = setTimeout(() => performContactSearch(query), 300);
}

const { getLabels } = useNativeFieldOptions();
const SOURCE_OPTIONS = getLabels('source');

const filteredAssignees = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value;
  return agents.value
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const filteredColumns = computed(() => {
  const q = columnSearch.value.toLowerCase();
  if (!q) return props.columns;
  return props.columns.filter(c => c.name.toLowerCase().includes(q));
});

const filteredSources = computed(() => {
  const q = sourceSearch.value.toLowerCase();
  if (!q) return SOURCE_OPTIONS;
  return SOURCE_OPTIONS.filter(s => s.toLowerCase().includes(q));
});

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  return (accountLabels.value ?? []).filter(l =>
    l.title.toLowerCase().includes(q)
  );
});

watch([filteredAssignees, assigneeSearch], () => {
  assigneeHighlight.value = 0;
});
watch([filteredColumns, columnSearch], () => {
  columnHighlight.value = 0;
});
watch([filteredSources, sourceSearch], () => {
  sourceHighlight.value = 0;
});

// ── Abrir/fechar submenus ─────────────────────────────────────────────────────
const SUBMENU_BTN_MAP = {
  column: { btnRef: columnBtnRef, posRef: columnSubmenuPos, width: 208 },
  status: { btnRef: statusBtnRef, posRef: statusSubmenuPos, width: 160 },
  assignee: { btnRef: assigneeBtnRef, posRef: assigneeSubmenuPos, width: 208 },
  date: { btnRef: dateBtnRef, posRef: dateSubmenuPos, width: 288 },
  value: { btnRef: valueBtnRef, posRef: valueSubmenuPos, width: 192 },
  source: { btnRef: sourceBtnRef, posRef: sourceSubmenuPos, width: 176 },
  tags_add: { btnRef: tagsAddBtnRef, posRef: tagsAddSubmenuPos, width: 176 },
  tags_remove: {
    btnRef: tagsRemoveBtnRef,
    posRef: tagsRemoveSubmenuPos,
    width: 176,
  },
  task: { btnRef: taskBtnRef, posRef: taskSubmenuPos, width: 224 },
  note: { btnRef: noteBtnRef, posRef: noteSubmenuPos, width: 224 },
  contact: { btnRef: contactBtnRef, posRef: contactSubmenuPos, width: 224 },
};

function openSubmenu(menu) {
  const closing = activeSubmenu.value === menu;
  activeSubmenu.value = closing ? null : menu;
  labelSearch.value = '';
  if (!closing) {
    const cfg = SUBMENU_BTN_MAP[menu];
    if (cfg) calcSubmenuPos(cfg.btnRef, cfg.posRef, cfg.width);
    if (menu === 'tags_add') nextTick(() => labelSearchRef.value?.focus());
    if (menu === 'tags_remove')
      nextTick(() => labelRemoveSearchRef.value?.focus());
  }
}

function setDelete() {
  activeSubmenu.value = null;
  pendingChanges.value = { delete: true };
}

// ── Execução ──────────────────────────────────────────────────────────────────
const isExecuting = ref(false);

async function execute() {
  if (!hasPendingChanges.value) return;
  isExecuting.value = true;
  try {
    const p = pendingChanges.value;
    const ids = props.selectedIds;

    if (p.delete) {
      await store.dispatch('kanban/bulkDeleteCards', ids);
    } else {
      const scalarFields = [
        'column_id',
        'status',
        'assignee_id',
        'contact_id',
        'closing_date',
        'closing_time',
        'value',
        'opp_source',
      ];
      const updatePayload = {};
      scalarFields.forEach(k => {
        if (Object.prototype.hasOwnProperty.call(p, k)) updatePayload[k] = p[k];
      });

      if (Object.keys(updatePayload).length > 0) {
        await store.dispatch('kanban/bulkUpdateCards', {
          ids,
          data: updatePayload,
        });
      }

      const labelsToAdd = p.label_add ?? [];
      const labelsToRemove = p.label_remove ?? [];
      await Promise.all([
        ...labelsToAdd.map(label =>
          store.dispatch('kanban/bulkLabelCards', { ids, label, action: 'add' })
        ),
        ...labelsToRemove.map(label =>
          store.dispatch('kanban/bulkLabelCards', {
            ids,
            label,
            action: 'remove',
          })
        ),
      ]);

      if (p.task?.title) {
        await store.dispatch('kanban/bulkCreateTask', { ids, task: p.task });
      }
      if (p.note?.content) {
        await store.dispatch('kanban/bulkCreateNote', {
          ids,
          content: p.note.content,
        });
      }
    }
    emit('done');
  } finally {
    isExecuting.value = false;
    pendingChanges.value = {};
  }
}

// ── Navegação por teclado — menu principal ────────────────────────────────────
const ALL_MAIN_ITEMS = [
  'column',
  'status',
  'assignee',
  'contact',
  'date',
  'value',
  'source',
  'tags_add',
  'tags_remove',
  'task',
  'note',
  'delete',
  'save',
];
const { isAdmin } = useAdmin();
// excluir card é só do admin (o backend também recusa)
const mainItems = computed(() =>
  ALL_MAIN_ITEMS.filter(item => item !== 'delete' || isAdmin.value)
);
const mainHighlight = ref(0);

function onMainKeydown(e) {
  if (activeSubmenu.value) return;
  if (e.key === 'Escape') {
    emit('close');
    return;
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    mainHighlight.value = Math.min(
      mainHighlight.value + 1,
      mainItems.value.length - 1
    );
    return;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    mainHighlight.value = Math.max(mainHighlight.value - 1, 0);
    return;
  }
  if (e.key === 'Enter') {
    e.preventDefault();
    const item = mainItems.value[mainHighlight.value];
    if (
      [
        'column',
        'status',
        'assignee',
        'contact',
        'date',
        'value',
        'source',
        'tags_add',
        'tags_remove',
        'task',
        'note',
      ].includes(item)
    )
      openSubmenu(item);
    else if (item === 'delete') setDelete();
    else if (item === 'save' && hasPendingChanges.value) execute();
  }
}

// ── Acumular alterações ───────────────────────────────────────────────────────
function setColumn(col) {
  activeSubmenu.value = null;
  pendingChanges.value = {
    ...pendingChanges.value,
    column_id: col.id,
    _columnName: col.name,
  };
}

function setAssignee(agent) {
  activeSubmenu.value = null;
  pendingChanges.value = {
    ...pendingChanges.value,
    assignee_id: agent?.id ?? null,
  };
}

function setContact(contact) {
  activeSubmenu.value = null;
  contactSearch.value = contact ? contact.name : '';
  contactResults.value = [];
  pendingChanges.value = {
    ...pendingChanges.value,
    contact_id: contact?.id ?? null,
    _contactName: contact?.name ?? null,
  };
}

function setSource(src) {
  activeSubmenu.value = null;
  pendingChanges.value = { ...pendingChanges.value, opp_source: src };
}

function onSubmenuKeydown(e) {
  const menu = activeSubmenu.value;
  if (!menu || !['assignee', 'column', 'source'].includes(menu)) return;
  const map = {
    assignee: {
      list: filteredAssignees,
      highlight: assigneeHighlight,
      search: assigneeSearch,
    },
    column: {
      list: filteredColumns,
      highlight: columnHighlight,
      search: columnSearch,
    },
    source: {
      list: filteredSources,
      highlight: sourceHighlight,
      search: sourceSearch,
    },
  };
  const ctx = map[menu];
  if (e.key === 'Escape') {
    activeSubmenu.value = null;
    ctx.search.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    ctx.highlight.value = Math.min(
      ctx.highlight.value + 1,
      ctx.list.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    ctx.highlight.value = Math.max(ctx.highlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = ctx.list.value[ctx.highlight.value];
    if (!item) return;
    if (menu === 'assignee') setAssignee(item);
    else if (menu === 'column') setColumn(item);
    else if (menu === 'source') setSource(item);
  } else if (e.key === 'Backspace') {
    ctx.search.value = ctx.search.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    ctx.search.value += e.key;
  }
}

onMounted(() => window.addEventListener('keydown', onMainKeydown));
onUnmounted(() => {
  window.removeEventListener('keydown', onMainKeydown);
  window.removeEventListener('keydown', onSubmenuKeydown);
  clearTimeout(contactSearchDebounce);
});

watch(activeSubmenu, (val, old) => {
  if (old === 'assignee') assigneeSearch.value = '';
  if (old === 'column') columnSearch.value = '';
  if (old === 'source') sourceSearch.value = '';
  if (old === 'contact') {
    contactSearch.value = '';
    contactResults.value = [];
  }
  if (val === null) {
    window.removeEventListener('keydown', onSubmenuKeydown);
    window.addEventListener('keydown', onMainKeydown);
  } else if (['assignee', 'column', 'source'].includes(val)) {
    window.removeEventListener('keydown', onMainKeydown);
    window.addEventListener('keydown', onSubmenuKeydown);
  } else {
    window.removeEventListener('keydown', onMainKeydown);
    window.removeEventListener('keydown', onSubmenuKeydown);
  }
});

function setStatus(status) {
  activeSubmenu.value = null;
  const extra =
    status === 'won'
      ? { closing_date: new Date().toISOString().slice(0, 10) }
      : {};
  pendingChanges.value = {
    ...pendingChanges.value,
    status,
    ...extra,
    delete: undefined,
  };
}

function setDate(val) {
  activeSubmenu.value = null;
  pendingChanges.value = {
    ...pendingChanges.value,
    closing_date: val.due_date ?? null,
    closing_time: val.due_time ?? null,
  };
}

function setValue(val) {
  pendingChanges.value = {
    ...pendingChanges.value,
    value: val === '' ? null : Number(val),
  };
}

function clearSource() {
  activeSubmenu.value = null;
  pendingChanges.value = { ...pendingChanges.value, opp_source: null };
}

function toggleLabelAdd(title) {
  const current = pendingChanges.value.label_add ?? [];
  pendingChanges.value = {
    ...pendingChanges.value,
    label_add: current.includes(title)
      ? current.filter(l => l !== title)
      : [...current, title],
  };
}

function toggleLabelRemove(title) {
  const current = pendingChanges.value.label_remove ?? [];
  pendingChanges.value = {
    ...pendingChanges.value,
    label_remove: current.includes(title)
      ? current.filter(l => l !== title)
      : [...current, title],
  };
}

// ── Tarefa e nota inline ──────────────────────────────────────────────────────
const taskForm = ref({
  title: '',
  task_type: 'general',
  due_date: '',
  assignee_id: null,
});
const noteForm = ref({ content: '' });

function confirmTask() {
  if (!taskForm.value.title.trim()) return;
  pendingChanges.value = {
    ...pendingChanges.value,
    task: { ...taskForm.value },
  };
  activeSubmenu.value = null;
}

function confirmNote() {
  if (!noteForm.value.content.trim()) return;
  pendingChanges.value = {
    ...pendingChanges.value,
    note: { content: noteForm.value.content },
  };
  activeSubmenu.value = null;
}

const TASK_TYPE_KEYS = [
  'general',
  'call',
  'email',
  'meeting',
  'follow_up',
  'whatsapp',
  'contract',
  'payment',
];
const TASK_TYPES = computed(() =>
  TASK_TYPE_KEYS.map(key => ({
    key,
    label: t(`KANBAN.BULK_ACTIONS.TASK_TYPE_${key.toUpperCase()}`),
  }))
);
</script>

<template>
  <div
    class="bg-n-background border border-n-weak rounded-xl shadow-xl w-64 py-1 overflow-hidden"
  >
    <!-- Cabeçalho -->
    <div
      class="flex items-center justify-between px-3 py-2 border-b border-n-weak"
    >
      <span class="text-xs font-semibold text-n-slate-11">{{
        t('KANBAN.BULK_ACTIONS.HEADER', { count })
      }}</span>
      <button
        class="text-n-slate-9 hover:text-n-slate-12 transition-colors"
        @click="emit('cancel')"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <!-- Mover para coluna -->
    <div>
      <button
        ref="columnBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'column' || pendingChanges.column_id
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('column')"
      >
        <span class="i-lucide-columns size-3.5 flex-shrink-0" />
        <span
          v-if="activeSubmenu === 'column' && columnSearch"
          class="text-n-slate-10 flex-1 text-left"
        >
          {{ columnSearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.MOVE_COLUMN')
        }}</span>
        <span
          v-if="pendingChanges._columnName"
          class="text-xs text-n-brand truncate max-w-20"
        >
          {{ pendingChanges._columnName }}
        </span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'column'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-52 py-1 overflow-hidden"
          :style="{
            top: columnSubmenuPos.top + 'px',
            left: columnSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <button
            v-for="(col, idx) in filteredColumns"
            :key="col.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              idx === columnHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : pendingChanges.column_id === col.id
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="setColumn(col)"
          >
            {{ col.name }}
            <span
              v-if="pendingChanges.column_id === col.id"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="columnSearch && !filteredColumns.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.BULK_ACTIONS.NOT_FOUND') }}
          </p>
        </div>
      </Teleport>
    </div>

    <!-- Mudar status -->
    <div>
      <button
        ref="statusBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'status' || pendingChanges.status
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('status')"
      >
        <span class="i-lucide-activity size-3.5 flex-shrink-0" />
        <span class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.CHANGE_STATUS')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'status'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-40 py-1 overflow-hidden"
          :style="{
            top: statusSubmenuPos.top + 'px',
            left: statusSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <button
            v-for="opt in [
              {
                key: 'open',
                label: t('KANBAN.BULK_ACTIONS.STATUS_OPEN'),
                icon: 'i-lucide-circle',
                color: 'text-n-blue-9',
              },
              {
                key: 'won',
                label: t('KANBAN.BULK_ACTIONS.STATUS_WON'),
                icon: 'i-lucide-circle-check',
                color: 'text-n-teal-9',
              },
              {
                key: 'lost',
                label: t('KANBAN.BULK_ACTIONS.STATUS_LOST'),
                icon: 'i-lucide-circle-x',
                color: 'text-n-ruby-9',
              },
            ]"
            :key="opt.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              pendingChanges.status === opt.key
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="setStatus(opt.key)"
          >
            <span
              class="size-3.5 flex-shrink-0"
              :class="[opt.icon, opt.color]"
            />
            {{ opt.label }}
            <span
              v-if="pendingChanges.status === opt.key"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
        </div>
      </Teleport>
    </div>

    <!-- Responsável -->
    <div>
      <button
        ref="assigneeBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'assignee' ||
          pendingChanges.assignee_id !== undefined
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('assignee')"
      >
        <span class="i-lucide-user size-3.5 flex-shrink-0" />
        <span
          v-if="activeSubmenu === 'assignee' && assigneeSearch"
          class="text-n-slate-10 flex-1 text-left"
        >
          {{ assigneeSearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.SET_ASSIGNEE')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'assignee'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-52 overflow-hidden py-1"
          :style="{
            top: assigneeSubmenuPos.top + 'px',
            left: assigneeSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <template v-if="!assigneeSearch">
            <button
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-11"
              @click="setAssignee(null)"
            >
              <span
                class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
              />
              {{ t('KANBAN.BULK_ACTIONS.REMOVE_ASSIGNEE') }}
            </button>
            <div class="h-px bg-n-weak my-1" />
          </template>
          <button
            v-for="(agent, idx) in filteredAssignees"
            :key="agent.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              idx === assigneeHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : pendingChanges.assignee_id === agent.id
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="setAssignee(agent)"
          >
            <div
              class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
              :style="
                agent.thumbnail
                  ? {
                      backgroundImage: `url(${agent.thumbnail})`,
                      backgroundSize: 'cover',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span v-if="!agent.thumbnail">{{ initials(agent.name) }}</span>
            </div>
            <span class="truncate">{{ agent.name }}</span>
            <span
              v-if="pendingChanges.assignee_id === agent.id"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="assigneeSearch && !filteredAssignees.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.BULK_ACTIONS.NOT_FOUND') }}
          </p>
        </div>
      </Teleport>
    </div>

    <!-- Atribuir ao contato -->
    <div>
      <button
        ref="contactBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'contact' || pendingChanges.contact_id !== undefined
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('contact')"
      >
        <span class="i-lucide-building-2 size-3.5 flex-shrink-0" />
        <span
          v-if="pendingChanges._contactName"
          class="flex-1 text-left truncate"
          >{{ pendingChanges._contactName }}</span
        >
        <span
          v-else-if="activeSubmenu === 'contact' && contactSearch"
          class="text-n-slate-10 flex-1 text-left truncate"
          >{{ contactSearch }}</span
        >
        <span v-else class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.SET_CONTACT')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'contact'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-56 overflow-hidden"
          :style="{
            top: contactSubmenuPos.top + 'px',
            left: contactSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <div class="p-1.5 border-b border-n-weak">
            <input
              v-model="contactSearch"
              class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
              :placeholder="t('KANBAN.BULK_ACTIONS.SEARCH_CONTACT')"
              @input="searchContacts(contactSearch)"
            />
          </div>
          <div class="max-h-52 overflow-y-auto py-1">
            <button
              v-if="!contactSearch"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-11"
              @click="setContact(null)"
            >
              <span
                class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
              />
              {{ t('KANBAN.BULK_ACTIONS.REMOVE_CONTACT') }}
            </button>
            <div v-if="!contactSearch" class="h-px bg-n-weak my-1" />
            <template v-if="contactSearching">
              <p class="px-3 py-2 text-sm text-n-slate-10 italic">
                {{ t('KANBAN.BULK_ACTIONS.SEARCHING') }}
              </p>
            </template>
            <template v-else-if="contactSearch && !contactResults.length">
              <p class="px-3 py-2 text-sm text-n-slate-10 italic">
                {{ t('KANBAN.BULK_ACTIONS.NOT_FOUND') }}
              </p>
            </template>
            <button
              v-for="(contact, idx) in contactResults"
              :key="contact.id"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
              :class="
                idx === contactHighlight
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : pendingChanges.contact_id === contact.id
                    ? 'text-n-brand font-medium'
                    : 'text-n-slate-12'
              "
              @click="setContact(contact)"
            >
              <div
                class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
                :style="
                  contact.thumbnail
                    ? {
                        backgroundImage: `url(${contact.thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : { backgroundColor: avatarColor(contact.name) }
                "
              >
                <span v-if="!contact.thumbnail">{{
                  initials(contact.name)
                }}</span>
              </div>
              <div class="flex flex-col items-start min-w-0">
                <span class="truncate text-xs font-medium leading-tight">{{
                  contact.name
                }}</span>
                <span
                  v-if="contact.company_name"
                  class="truncate text-[10px] text-n-slate-10 leading-tight"
                  >{{ contact.company_name }}</span
                >
              </div>
              <span
                v-if="pendingChanges.contact_id === contact.id"
                class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- Previsão de fechamento -->
    <div>
      <button
        ref="dateBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'date' || pendingChanges.closing_date !== undefined
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('date')"
      >
        <span class="i-lucide-calendar size-3.5 flex-shrink-0" />
        <span class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.CLOSING_DATE')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'date'"
          data-bulk-dropdown
          class="fixed z-[10020]"
          :style="{
            top: dateSubmenuPos.top + 'px',
            left: dateSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <KanbanDatePicker
            :model-value="dateValue"
            @update:model-value="setDate"
          />
        </div>
      </Teleport>
    </div>

    <!-- Valor -->
    <div>
      <button
        ref="valueBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'value' ||
          Object.prototype.hasOwnProperty.call(pendingChanges, 'value')
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('value')"
      >
        <span class="i-lucide-banknote size-3.5 flex-shrink-0" />
        <span class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.CHANGE_VALUE')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'value'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-48 p-3"
          :style="{
            top: valueSubmenuPos.top + 'px',
            left: valueSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <input
            type="number"
            step="0.01"
            min="0"
            :placeholder="t('KANBAN.BULK_ACTIONS.VALUE_PLACEHOLDER')"
            class="w-full text-sm px-2 py-1.5 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none border border-n-weak focus:border-n-brand"
            :value="pendingChanges.value ?? ''"
            @input="setValue($event.target.value)"
          />
          <button
            class="mt-2 text-xs text-n-ruby-11 hover:text-n-ruby-9 transition-colors"
            @click="
              () => {
                pendingChanges.value = { ...pendingChanges.value, value: null };
                activeSubmenu.value = null;
              }
            "
          >
            {{ t('KANBAN.BULK_ACTIONS.REMOVE_VALUE') }}
          </button>
        </div>
      </Teleport>
    </div>

    <!-- Fonte -->
    <div>
      <button
        ref="sourceBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'source' ||
          Object.prototype.hasOwnProperty.call(pendingChanges, 'opp_source')
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('source')"
      >
        <span class="i-lucide-megaphone size-3.5 flex-shrink-0" />
        <span
          v-if="activeSubmenu === 'source' && sourceSearch"
          class="text-n-slate-10 flex-1 text-left"
        >
          {{ sourceSearch }}
        </span>
        <span v-else class="flex-1 text-left">{{
          t('KANBAN.BULK_ACTIONS.CHANGE_SOURCE')
        }}</span>
        <span class="i-lucide-chevron-right size-3.5 flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'source'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-44 py-1 overflow-hidden"
          :style="{
            top: sourceSubmenuPos.top + 'px',
            left: sourceSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <button
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-11"
            @click="clearSource"
          >
            <span class="i-lucide-x size-3.5 text-n-slate-9 flex-shrink-0" />
            {{ t('KANBAN.BULK_ACTIONS.REMOVE_SOURCE') }}
          </button>
          <div class="h-px bg-n-weak my-1" />
          <button
            v-for="(src, idx) in filteredSources"
            :key="src"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              idx === sourceHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : pendingChanges.opp_source === src
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="setSource(src)"
          >
            {{ src }}
            <span
              v-if="pendingChanges.opp_source === src"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
        </div>
      </Teleport>
    </div>

    <div class="h-px bg-n-weak mx-2 my-1" />

    <!-- Inserir tags -->
    <div>
      <button
        ref="tagsAddBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'tags_add' || pendingChanges.label_add?.length
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('tags_add')"
      >
        <span class="i-lucide-tag size-3.5 flex-shrink-0" />
        {{ t('KANBAN.BULK_ACTIONS.ADD_TAGS') }}
        <span
          v-if="pendingChanges.label_add?.length"
          class="ml-1 text-xs bg-n-brand text-white rounded-full px-1.5 py-px"
        >
          {{ pendingChanges.label_add.length }}
        </span>
        <span class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'tags_add'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden"
          :style="{
            top: tagsAddSubmenuPos.top + 'px',
            left: tagsAddSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <div class="p-1.5 border-b border-n-weak">
            <input
              ref="labelSearchRef"
              v-model="labelSearch"
              class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
              :placeholder="t('KANBAN.BULK_ACTIONS.SEARCH')"
            />
          </div>
          <div class="max-h-48 overflow-y-auto py-1">
            <button
              v-for="lbl in filteredLabels"
              :key="lbl.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-n-alpha-1"
              :class="
                pendingChanges.label_add?.includes(lbl.title)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="toggleLabelAdd(lbl.title)"
            >
              <span
                class="size-2 rounded-full flex-shrink-0"
                :style="{ backgroundColor: lbl.color }"
              />
              {{ lbl.title }}
              <span
                v-if="pendingChanges.label_add?.includes(lbl.title)"
                class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- Remover tags -->
    <div>
      <button
        ref="tagsRemoveBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'tags_remove' || pendingChanges.label_remove?.length
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('tags_remove')"
      >
        <span class="relative flex-shrink-0 size-3.5">
          <span class="i-lucide-tag size-3.5" />
          <span class="absolute inset-0 flex items-center justify-center">
            <span
              class="block w-[18px] h-px bg-current rotate-[-45deg] translate-x-[-1px]"
            />
          </span>
        </span>
        {{ t('KANBAN.BULK_ACTIONS.REMOVE_TAGS') }}
        <span
          v-if="pendingChanges.label_remove?.length"
          class="ml-1 text-xs bg-n-ruby-9 text-white rounded-full px-1.5 py-px"
        >
          {{ pendingChanges.label_remove.length }}
        </span>
        <span class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0" />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'tags_remove'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden"
          :style="{
            top: tagsRemoveSubmenuPos.top + 'px',
            left: tagsRemoveSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <div class="p-1.5 border-b border-n-weak">
            <input
              ref="labelRemoveSearchRef"
              v-model="labelSearch"
              class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
              :placeholder="t('KANBAN.BULK_ACTIONS.SEARCH')"
            />
          </div>
          <div class="max-h-48 overflow-y-auto py-1">
            <button
              v-for="lbl in filteredLabels"
              :key="lbl.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-n-alpha-1"
              :class="
                pendingChanges.label_remove?.includes(lbl.title)
                  ? 'text-n-ruby-11 font-medium'
                  : 'text-n-slate-12'
              "
              @click="toggleLabelRemove(lbl.title)"
            >
              <span
                class="size-2 rounded-full flex-shrink-0"
                :style="{ backgroundColor: lbl.color }"
              />
              {{ lbl.title }}
              <span
                v-if="pendingChanges.label_remove?.includes(lbl.title)"
                class="i-lucide-check size-3 ml-auto text-n-ruby-9 flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </Teleport>
    </div>

    <div class="h-px bg-n-weak mx-2 my-1" />

    <!-- Adicionar tarefa -->
    <div>
      <button
        ref="taskBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'task' || pendingChanges.task?.title
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('task')"
      >
        <span class="i-lucide-circle-check size-3.5 flex-shrink-0" />
        {{ t('KANBAN.BULK_ACTIONS.ADD_TASK') }}
        <span
          v-if="pendingChanges.task?.title"
          class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
        />
        <span
          v-else
          class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0"
        />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'task'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-56 p-3 flex flex-col gap-2"
          :style="{
            top: taskSubmenuPos.top + 'px',
            left: taskSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <input
            v-model="taskForm.title"
            type="text"
            :placeholder="t('KANBAN.BULK_ACTIONS.TASK_TITLE_PLACEHOLDER')"
            class="w-full text-sm px-2 py-1.5 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none border border-n-weak focus:border-n-brand"
          />
          <select
            v-model="taskForm.task_type"
            class="w-full text-sm px-2 py-1.5 rounded-lg bg-n-surface-2 text-n-slate-12 outline-none border border-n-weak focus:border-n-brand"
          >
            <option v-for="tt in TASK_TYPES" :key="tt.key" :value="tt.key">
              {{ tt.label }}
            </option>
          </select>
          <input
            v-model="taskForm.due_date"
            type="date"
            class="w-full text-sm px-2 py-1.5 rounded-lg bg-n-surface-2 text-n-slate-12 outline-none border border-n-weak focus:border-n-brand"
          />
          <button
            class="w-full py-1.5 text-sm font-medium rounded-lg transition-all"
            :class="
              taskForm.title.trim()
                ? 'bg-n-brand text-white hover:brightness-110'
                : 'bg-n-surface-2 text-n-slate-9 cursor-not-allowed'
            "
            :disabled="!taskForm.title.trim()"
            @click="confirmTask"
          >
            {{ t('KANBAN.BULK_ACTIONS.CONFIRM') }}
          </button>
        </div>
      </Teleport>
    </div>

    <!-- Adicionar nota -->
    <div>
      <button
        ref="noteBtnRef"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
        :class="
          activeSubmenu === 'note' || pendingChanges.note?.content
            ? 'text-n-brand font-medium'
            : 'text-n-slate-12'
        "
        @click="openSubmenu('note')"
      >
        <span class="i-lucide-sticky-note size-3.5 flex-shrink-0" />
        {{ t('KANBAN.BULK_ACTIONS.ADD_NOTE') }}
        <span
          v-if="pendingChanges.note?.content"
          class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
        />
        <span
          v-else
          class="i-lucide-chevron-right size-3.5 ml-auto flex-shrink-0"
        />
      </button>
      <Teleport to="body">
        <div
          v-if="activeSubmenu === 'note'"
          data-bulk-dropdown
          class="fixed z-[10020] bg-n-background border border-n-weak rounded-xl shadow-xl w-56 p-3 flex flex-col gap-2"
          :style="{
            top: noteSubmenuPos.top + 'px',
            left: noteSubmenuPos.left + 'px',
          }"
          @click.stop
        >
          <textarea
            v-model="noteForm.content"
            rows="3"
            :placeholder="t('KANBAN.BULK_ACTIONS.NOTE_PLACEHOLDER')"
            class="w-full text-sm px-2 py-1.5 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none border border-n-weak focus:border-n-brand resize-none"
          />
          <button
            class="w-full py-1.5 text-sm font-medium rounded-lg transition-all"
            :class="
              noteForm.content.trim()
                ? 'bg-n-brand text-white hover:brightness-110'
                : 'bg-n-surface-2 text-n-slate-9 cursor-not-allowed'
            "
            :disabled="!noteForm.content.trim()"
            @click="confirmNote"
          >
            {{ t('KANBAN.BULK_ACTIONS.CONFIRM') }}
          </button>
        </div>
      </Teleport>
    </div>

    <div v-if="isAdmin" class="h-px bg-n-weak mx-2 my-1" />

    <!-- Excluir -->
    <button
      v-if="isAdmin"
      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-ruby-2 transition-colors"
      :class="
        pendingChanges.delete
          ? 'text-n-ruby-11 font-medium bg-n-ruby-2'
          : 'text-n-ruby-11'
      "
      @click="setDelete"
    >
      <span class="i-lucide-trash-2 size-3.5 flex-shrink-0" />
      {{ t('KANBAN.BULK_ACTIONS.DELETE_CARDS') }}
      <span
        v-if="pendingChanges.delete"
        class="i-lucide-check size-3 ml-auto text-n-ruby-9 flex-shrink-0"
      />
    </button>

    <!-- Rodapé: Aplicar -->
    <div class="flex justify-end px-3 py-2 border-t border-n-weak mt-1">
      <button
        class="px-3 py-1.5 text-xs font-medium rounded-lg transition-all"
        :class="
          hasPendingChanges
            ? 'bg-n-brand text-white hover:brightness-110 cursor-pointer'
            : 'bg-n-surface-2 text-n-slate-9 cursor-not-allowed'
        "
        :disabled="!hasPendingChanges || isExecuting"
        @click="hasPendingChanges && execute()"
      >
        <span
          v-if="isExecuting"
          class="i-lucide-loader size-3 animate-spin inline-block mr-1"
        />
        {{ t('KANBAN.BULK_ACTIONS.APPLY') }}
      </button>
    </div>
  </div>
</template>
